// Static product config — ported from the legacy v5.5 artifact's baked
// `<script id="data" type="application/json">` element (RAW.products / _C.p).
// See REBUILD-SPEC.md §1.1.
//
// Each product keeps id/short/region from the source, and we ADD `notionTour`:
// the canonical Notion TOUR-select label (FLG/PSCT/TUS prefix + tour name).
// NOTE on notionTour vs the legacy availability layer:
//   - The legacy availability layer (`AVAIL_PROD_TO_ID`) used "TUC ..." prefixes
//     for Tucson and "FLG Madness" for Mountain Town of Madness. Per REBUILD-SPEC
//     §2, Tucson is STANDARDIZED to "TUS" everywhere, so the canonical notionTour
//     labels below use "TUS Mural" / "TUS Haunted History".
//   - durationMin is NOT present in the source for any product, so it is omitted
//     (the spec says: omit absent optional fields rather than guessing).

import type { Product } from "@/types";

/**
 * The 8 bookable tour products, keyed by Peek productId.
 * IDs cross-checked against REBUILD-SPEC §1.1 — all 8 match exactly.
 * (The Pub Crawl is NOT in this list; its id is exported separately as
 *  PUB_CRAWL_ID below, matching the legacy RAW.pubCrawlId.)
 */
export const PRODUCTS: Product[] = [
  {
    id: "80a1896b-62c1-4d84-a6f4-c8ddf65ff074",
    short: "FLG Mural",
    region: "Flagstaff",
    notionTour: "FLG Mural",
  },
  {
    id: "0ced34cb-0eb9-4f59-bc48-f5cb72b72187",
    short: "FLG Mountain Town", // "Mountain Town of Madness Adults-Only Tour"
    region: "Flagstaff",
    // Legacy availability layer labeled this "FLG Madness". OPEN QUESTION
    // (REBUILD-SPEC §10.4): Peek groups Mountain Town of Madness with Prescott
    // despite the FLG-coded short — market may need to flip to Prescott.
    notionTour: "FLG Madness",
  },
  {
    id: "b419e3fd-85f0-416d-9018-6a6f5d0426fd",
    short: "FLG Route 66",
    region: "Flagstaff",
    notionTour: "FLG Route 66",
  },
  {
    id: "1898909d-ba15-436a-8c4f-452945d70f9f",
    short: "FLG Haunted History",
    region: "Flagstaff",
    notionTour: "FLG Haunted History",
  },
  {
    id: "8d66ea83-c5af-42ea-90ec-9ba78f05d7f2",
    // Legacy short was "PRSCT Haunted History"; standardized prefix is PSCT.
    short: "PSCT Haunted History",
    region: "Prescott",
    notionTour: "PSCT Haunted History",
  },
  {
    id: "0bc9822a-4109-461c-89bf-41ac494c61f7",
    short: "PSCT History", // legacy short was "PRSCT History"
    region: "Prescott",
    notionTour: "PSCT History",
  },
  {
    id: "2dae269d-e8a0-4afb-a926-0bae94468d37",
    short: "TUS Mural",
    region: "Tucson",
    // Legacy availability layer used "TUC Mural"; canonicalized to TUS.
    notionTour: "TUS Mural",
  },
  {
    id: "7c6e9626-35fd-4e72-887a-f8fb29a04cb8",
    short: "TUS Haunted History",
    region: "Tucson",
    // Legacy availability layer used "TUC Haunted History"; canonicalized to TUS.
    notionTour: "TUS Haunted History",
  },
];

/**
 * Pub Crawl ("Spirits w/ the Spirits") Peek productId. Reference only — no code
 * consumes it yet; kept because it is a real product in the Worker's canon that
 * the picker deliberately excludes (not in the 8-product list).
 */
export const PUB_CRAWL_ID = "43256f8e-fefc-4292-8c18-3322fc0dac64";

/** productId -> Product lookup. */
export const productById: Record<string, Product> = Object.fromEntries(
  PRODUCTS.map((p) => [p.id, p]),
);
